<template>
  <div class="mec-wrap">
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary mb-4">
      <div class="container-xl">
        <router-link to="/" class="navbar-brand">
          <img src="../assets/GDQ-White.svg" alt="GDQ" style="max-height: 30px" />
        </router-link>
      </div>
    </nav>

    <div class="container-xl text-container mec-single-event">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">{{ t(T.loading) }}</p>
      </div>

      <!-- Not found -->
      <div v-else-if="!event" class="text-center py-5">
        <p class="text-muted">{{ t(T.not_found) }}</p>
        <router-link to="/" class="btn btn-outline-primary btn-sm">{{ t(T.back_home) }}</router-link>
      </div>

      <template v-else>
        <h2>{{ event.title }}</h2>
        <p class="text-muted">{{ eventDates }}<template v-if="event.location_name"> · {{ event.location_name }}</template></p>
        <div v-if="event.description" class="mb-4" v-html="event.description"></div>

        <!-- Booking status gate -->
        <div v-if="event.booking_status !== 'open'" class="alert alert-warning">
          {{ event.booking_message }}
        </div>

        <!-- ================= CHECKOUT ================= -->
        <div v-else-if="view === 'checkout' && booking" class="gdq-flow-col">
          <h4>{{ t(T.checkout_heading) }}</h4>
          <p>{{ t(T.review) }}</p>
          <p class="has-vivid-red-color has-text-color">{{ t(T.note_heading) }}</p>

          <div class="mec-wrap mec-cart">
            <table id="mec_cart_transactions_table">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ t(T.th_transaction_id) }}</th>
                  <th>{{ t(T.th_event) }}</th>
                  <th>{{ t(T.th_tickets) }}</th>
                  <th>{{ t(T.th_dates) }}</th>
                  <th>{{ t(T.th_payable) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td>{{ booking.reference }}</td>
                  <td class="mec-cart-event-info">
                    <a :href="config.web_url + '/event/' + event.id" target="_blank">{{ event.title }}</a>
                  </td>
                  <td>
                    <ul>
                      <li v-for="(a, i) in attendees" :key="i">
                        <h5>{{ ticketName }}</h5>
                        <h6>{{ a.first_name }} {{ a.last_name }}</h6>
                      </li>
                    </ul>
                  </td>
                  <td>
                    <ul><li>{{ eventDates }}</li></ul>
                  </td>
                  <td><span class="mec-transaction-price">{{ priceText(priceTotal) }}</span></td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="5"></th>
                  <th>{{ priceText(priceTotal) }}</th>
                </tr>
              </tfoot>
            </table>
            <div class="mec-cart-coupon-checkout-action">
              <div class="mec-cart-checkout-button">
                    <a href="#gdq-gateway-section" class="gdq-btn gdq-btn-primary gdq-btn-block" @click.prevent="scrollToGateway">{{ t(T.proceed) }}</a>
              </div>
            </div>
          </div>

          <div id="gdq-gateway-section" class="mec-wrap mec-checkout">
            <div class="mec-checkout-footer">
              <p id="mec_cart_total_payable"><span class="mec-checkout-price-wrapper">{{ priceText(priceTotal) }}</span></p>
              <div class="mec-events-meta-group-booking mec-checkout-gateways-wrapper mec-book-form-gateways">
                <div class="mec-checkout-form-gateway-label"><label>{{ t(T.faktura) }}</label></div>
                <div class="mec-checkout-form-gateway-checkout">
                  <p class="mec-gateway-comment">{{ t(T.faktura_note) }}</p>
                  <button class="gdq-btn gdq-btn-primary gdq-btn-block" type="button" @click="completeBooking">{{ t(T.submit) }}</button>
                </div>
              </div>
              <div class="mec-checkout-actions">
                <div class="mec-checkout-cart-button">
                    <a href="#" class="gdq-btn gdq-btn-secondary gdq-btn-block" @click.prevent="scrollToTop">{{ t(T.back_to_cart) }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ================= STEP 2: ATTENDEE'S FORM ================= -->
        <form id="gdq-book-form" v-else-if="view === 'form' && step === 2" class="gdq-book-card gdq-flow-col mec-booking-form-container mec-events-meta-group-booking" @submit.prevent="addToCart">
          <h4 class="gdq-book-heading">{{ doneNote.attendee_heading }}</h4>

          <ul class="mec-book-tickets-container">
            <li v-for="(a, ai) in attendees" :key="ai" class="mec-book-ticket-container">
              <div v-if="attendees.length > 1" class="gdq-attendee-heading">
                <h4>{{ t(T.attendee_of, { n: ai + 1, m: attendees.length }) }}</h4>
              </div>

              <ul class="mec-book-tickets-reg-fields-container">
                <li
                  v-for="(f, fi) in fields"
                  :key="fi"
                  :class="fieldLiClass(f, ai, fi)"
                >
                  <!-- name -->
                  <template v-if="f.type === 'name'">
                    <label style="display:block" class="required">
                      {{ t(T.first_name) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </label>
                    <span class="mec-field-wrapper mec-book-name-field-wrapper">
                      <span class="mec-field-icon mec-book-name-field-icon" v-html="ICONS.user"></span>
                      <input v-model.trim="a.first_name" type="text" style="width: 95% !important" :placeholder="t(T.first_name)" autocomplete="given-name" :required="!!f.mandatory" :class="{ 'is-invalid': err(ai, fi) }" @input="clearErr(ai, fi)">
                    </span>
                    <label style="display:block" class="required">
                      {{ t(T.family_name) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="errLast(ai, fi)" class="mec-booking-field-required">{{ errLast(ai, fi) }}</span>
                    </label>
                    <span class="mec-field-wrapper mec-book-name-field-wrapper">
                      <span class="mec-field-icon mec-book-name-field-icon" v-html="ICONS.user"></span>
                      <input v-model.trim="a.last_name" type="text" style="width: 95% !important" :placeholder="t(T.family_name)" autocomplete="family-name" :required="!!f.mandatory" :class="{ 'is-invalid': errLast(ai, fi) }" @input="clearErrLast(ai, fi)">
                    </span>
                  </template>

                  <!-- email -->
                  <template v-else-if="f.type === 'mec_email'">
                    <label style="display:block" class="required">
                      {{ fieldLabel(f) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </label>
                    <span class="mec-field-wrapper mec-book-email-field-wrapper">
                      <span class="mec-field-icon mec-book-email-field-icon" v-html="ICONS.email"></span>
                      <input v-model.trim="a.email" type="email" style="width: 95% !important" :placeholder="fieldLabel(f)" :required="!!f.mandatory" :class="{ 'is-invalid': err(ai, fi) }" @input="clearErr(ai, fi)">
                    </span>
                  </template>

                    <!-- paragraph (bare p, WP parity) -->
                    <template v-else-if="f.type === 'p'">
                      <p>{{ f.content || fieldLabel(f) }}</p>
                    </template>

                  <!-- text -->
                  <template v-else-if="f.type === 'text'">
                    <label style="display:block" class="required">
                      {{ fieldLabel(f) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </label>
                      <input v-model.trim="a.data['f' + fi]" type="text" :placeholder="fieldLabel(f)" :required="!!f.mandatory" :class="{ 'is-invalid': err(ai, fi) }" @input="clearErr(ai, fi)">
                  </template>

                  <!-- textarea -->
                  <template v-else-if="f.type === 'textarea'">
                    <label style="display:block" class="required">
                      {{ fieldLabel(f) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </label>
                      <textarea v-model="a.data['f' + fi]" rows="4" :placeholder="fieldLabel(f)" :required="!!f.mandatory" :class="{ 'is-invalid': err(ai, fi) }" @input="clearErr(ai, fi)"></textarea>
                  </template>

                    <!-- radio (WP parity: no outer label, inline-block option labels) -->
                    <template v-else-if="f.type === 'radio'">
                      <span v-if="fieldLabel(f)" style="display:block" class="required">
                        {{ fieldLabel(f) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span>
                      </span>
                      <label v-for="(o, oi) in (f.options || [])" :key="oi">
                        <input type="radio" :name="'gdqf-' + ai + '-' + fi" :value="optionText(o)" v-model="a.data['f' + fi]" @change="clearErr(ai, fi)">
                        {{ optionText(o) }}
                      </label>
                      <span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </template>

                    <!-- checkbox (multi, WP parity: block outer label + block option labels) -->
                    <template v-else-if="f.type === 'checkbox'">
                      <label style="display:block" class="required">
                        {{ fieldLabel(f) }}<span v-if="f.mandatory" class="wbmec-mandatory">*</span><span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                      </label>
                      <label v-for="(o, oi) in (f.options || [])" :key="oi">
                        <input type="checkbox" :value="optionText(o)" v-model="a.data['f' + fi]" @change="clearErr(ai, fi)">
                        {{ optionText(o) }}
                      </label>
                    </template>

                    <!-- agreement (WP parity: checkbox + * + text inside one label) -->
                    <template v-else-if="f.type === 'agreement'">
                      <label class="required">
                        <input type="checkbox" v-model="a.data['f' + fi]" @change="clearErr(ai, fi)">
                        <span v-if="f.mandatory" class="wbmec-mandatory">*</span>
                        <span v-html="agreementHtml(f)"></span>
                      </label>
                      <span v-if="err(ai, fi)" class="mec-booking-field-required">{{ err(ai, fi) }}</span>
                    </template>
                </li>
              </ul>
            </li>
          </ul>

          <div v-if="error" class="alert alert-danger">{{ error }}</div>

          <div class="gdq-form-btns">
            <button id="mec-book-form-btn-step-2" class="gdq-btn gdq-btn-primary" type="submit" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>{{ saving ? t(T.saving) : doneNote.book_button }}
            </button>
            <button id="mec-book-form-back-btn-step-2" class="gdq-btn gdq-btn-secondary" type="button" @click="goBackToStep1"><i class="fas fa-arrow-left" aria-hidden="true"></i> {{ t(T.back) }}</button>
          </div>
        </form>

        <!-- ================= STEP 1: BOOK EVENT ================= -->
        <div id="gdq-book-card" v-else-if="view === 'form'" class="gdq-book-card gdq-flow-col">
          <form @submit.prevent="goAttendees">
            <h4 class="gdq-book-heading">{{ doneNote.book_heading }}</h4>

            <div class="gdq-book-rows">
              <div class="gdq-book-row">
                <span class="gdq-book-row-label">{{ t(T.price_per_ticket) }}</span>
                <span class="gdq-book-row-value">{{ event.price > 0 ? priceText(event.price) : t(T.free) }}</span>
              </div>

              <div class="gdq-book-row">
                <span class="gdq-book-row-label">{{ t(T.quantity) }}</span>
                <div class="gdq-qty-stepper">
                  <button type="button" class="gdq-qty-btn" aria-label="Fewer" @click="decQty" :disabled="qty <= 0">&minus;</button>
                  <input v-model.number="qty" type="number" class="gdq-qty-input" inputmode="numeric" :min="0" :max="event.available">
                  <button type="button" class="gdq-qty-btn" aria-label="More" @click="incQty" :disabled="qty >= (event.available || 0)">+</button>
                </div>
              </div>

              <div class="gdq-book-row gdq-book-row-total">
                <span class="gdq-book-row-label">{{ t(T.total) }}</span>
                <span class="gdq-book-row-value">{{ priceText(priceTotal) }}</span>
              </div>
            </div>

            <div class="gdq-book-avail">
              <span class="gdq-avail-dot" aria-hidden="true"></span>
              {{ t(T.spots_available, { n: event.available }) }}
            </div>

            <button id="mec-book-form-btn-step-1" class="gdq-btn gdq-btn-primary gdq-btn-block" type="submit" :disabled="qty <= 0">
              {{ t(T.continue) }} <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </button>
          </form>
        </div>

        <!-- ================= CONFIRMATION ================= -->
        <div v-else-if="view === 'done' && booking" class="card shadow-sm gdq-flow-col">
          <div class="card-body gdq-confirm-body text-center">
            <div class="gdq-confirm-check" aria-hidden="true"><i class="fas fa-check"></i></div>
            <h4 class="gdq-confirm-title mb-3">{{ doneNote.title }}</h4>
            <p class="text-muted mb-2">
              <template v-if="event.is_virtual">{{ doneNote.note }}</template>
              <template v-else>{{ eventDates }}<span v-if="event.location_name"> · {{ event.location_name }}</span><template v-if="doneNote.note"><br>{{ doneNote.note }}</template></template>
            </p>
            <p v-if="doneNote.share" class="mb-2">{{ doneNote.share }}</p>
            <p class="mb-0"><strong>{{ doneNote.reference }}:</strong> {{ booking.reference }}</p>
          </div>
          <div class="card-footer bg-white">
            <h6>{{ doneNote.attendees }}</h6>
            <table class="table table-sm mb-2">
              <thead>
                <tr>
                  <th>{{ doneNote.th_name }}</th>
                  <th>{{ doneNote.th_email }}</th>
                  <th>{{ doneNote.th_qty }}</th>
                  <th>{{ doneNote.th_price }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, i) in booking.attendees" :key="i">
                  <td>{{ a.name || (a.first_name + ' ' + a.last_name) }}</td>
                  <td>{{ a.email }}</td>
                  <td>{{ a.quantity }}</td>
                  <td>{{ a.price != null ? formatPrice(a.price) + ' ' + currencyLabel : (booking.attendees.length === 1 ? formatPrice(booking.price_total) + ' ' + currencyLabel : '') }}</td>
                </tr>
              </tbody>
            </table>
            <p class="text-muted small mb-0">
              {{ doneNote.email_sent }} <strong>{{ booking.attendees[0].email }}</strong>.
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { config } from '../../config';

import { ICONS } from './EventBookingIcons';

export default {
  name: 'EventBooking',
  setup() {
    const route = useRoute();
    const store = useStore();

    const lang = computed(() => (route.query.lang ? String(route.query.lang).substring(0, 2) : 'en'));
    const loading = ref(true);
    const event = ref(null);
    const error = ref('');

    const view = ref('form');
    const step = ref(1);
    const qty = ref(1);
    const attendees = reactive([]);
    const fieldErrs = reactive({});
    const saving = ref(false);
    const booking = ref(null);

    const texts = ref({});
    const T = {
      loading: 1, not_found: 2, back_home: 3, price_per_ticket: 4, free: 5, quantity: 6, total: 7,
      spots_available: 8, continue: 9, attendee_of: 10, first_name: 11, family_name: 12, back: 13,
      saving: 14, required: 15, email_invalid: 16, book_heading: 17, attendee_heading: 18,
      book_button: 19, checkout_heading: 20, review: 21, note_heading: 22, th_transaction_id: 23,
      th_event: 24, th_tickets: 25, th_dates: 26, th_payable: 27, proceed: 28, faktura: 29,
      faktura_note: 30, submit: 31, back_to_cart: 32, done_title: 33, done_note_virtual: 34,
      done_note_physical: 35, done_share: 36, done_reference: 37, done_attendees: 38,
      done_email_sent: 39, th_name: 40, th_email: 41, th_qty: 42, th_price: 43, booking_failed: 49
    };
    const T_STR = {
      1: 'Loading event...', 2: 'Event not found', 3: 'Back to Home', 4: 'Price per ticket',
      5: 'Free', 6: 'Quantity', 7: 'Total', 8: '{n} spots available', 9: 'Continue',
      10: 'Attendee {n} of {m}', 11: 'First name', 12: 'Family name', 13: 'Back',
      14: 'Saving…', 15: 'This field is required.', 16: 'Email format is not valid.',
      17: 'Book your place', 18: "Who's attending?", 19: 'Book', 20: 'Booking',
      21: 'Please review the information below.',
      22: 'Note! To complete your booking, click the button below.',
      23: 'Transaction ID', 24: 'Event', 25: 'Tickets', 26: 'Dates', 27: 'Payable',
      28: 'Proceed to checkout', 29: 'Invoice', 30: 'The invoice is sent to your invoice address.',
      31: 'Submit', 32: 'Back to Cart', 33: 'Booking confirmed', 34: '', 35: '', 36: '',
      37: 'Reference', 38: 'Attendees', 39: 'A confirmation email has been sent to',
      40: 'Name', 41: 'Email', 42: 'Qty', 43: 'Price', 49: 'Booking failed'
    };
    function t(k, vars = {}) {
      let s = (texts.value && texts.value[k]) || T_STR[k] || '';
      for (const [key, val] of Object.entries(vars)) s = s.split('{'+key+'}').join(String(val));
      return s;
    }

    const ticketName = computed(() => (event.value ? event.value.title : ''));

    const doneNote = computed(() => {
      const e = event.value || {};
      return {
        title: t(T.done_title),
        note: t(e.is_virtual ? T.done_note_virtual : T.done_note_physical),
        share: t(T.done_share),
        reference: t(T.done_reference),
        attendees: t(T.done_attendees),
        email_sent: t(T.done_email_sent),
        th_name: t(T.th_name),
        th_email: t(T.th_email),
        th_qty: t(T.th_qty),
        th_price: t(T.th_price),
        book_heading: t(T.book_heading),
        attendee_heading: t(T.attendee_heading),
        book_button: t(T.book_button)
      };
    });

    const currencyLabel = computed(() => {
      const c = event.value && event.value.currency;
      return (!c || c === 'SEK') ? 'kr' : c;
    });

    function formatPrice(p) {
      const n = Number(p);
      if (isNaN(n)) return String(p);
      const locale = lang.value === 'sv' ? 'sv-SE' : 'en-US';
      return new Intl.NumberFormat(locale, { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: Number.isInteger(n) ? 0 : 2 }).format(n);
    }

    function priceText(p) {
      return formatPrice(p) + ' ' + currencyLabel.value;
    }

    const eventDates = computed(() => {
      const e = event.value;
      if (!e) return '';
      const locale = lang.value === 'sv' ? 'sv-SE' : 'en-US';
      const tz = config.timeZone;
      const day = (dt) => dt ? new Date(dt).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: tz }) : '';
      const tim = (dt) => dt ? new Date(dt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }) : '';
      if (e.all_day) {
        return (day(e.start_dt) + (e.end_dt && e.end_dt !== e.start_dt ? ' - ' + day(e.end_dt) : '')).trim();
      }
      return (day(e.start_dt) + (e.start_dt ? ' ' + tim(e.start_dt) : '') + (e.end_dt ? ' - ' + tim(e.end_dt) : '')).trim();
    });

    const priceTotal = computed(() => Number((event.value && event.value.price) || 0) * qty.value);

    const fields = computed(() => {
      const ff = (event.value && event.value.form_fields && event.value.form_fields.fields) || [];
      const out = ff.map(f => ({ ...f }));
      if (!out.some(f => f.type === 'name')) {
        out.unshift({ type: 'name', mandatory: true, label: { en: 'Full name', sv: 'Namn' } });
      }
      if (!out.some(f => f.type === 'mec_email')) {
        const i = out.findIndex(f => f.type === 'name');
        out.splice(i + 1, 0, { type: 'mec_email', mandatory: true, label: { en: 'E-mail', sv: 'E-post' } });
      }
      return out;
    });

    function fieldLabel(f) {
      const l = f.label || {};
      return l[lang.value] || l.en || l.sv || '';
    }

    function optionText(o) {
      if (!o) return '';
      if (typeof o === 'string') return o;
      const l = (o.label !== undefined && o.label !== null) ? o.label : o;
      if (typeof l === 'string') return l;
      return l[lang.value] || l.en || l.sv || '';
    }

    function agreementHtml(f) {
      const privacy = lang.value === 'sv'
        ? '<a href="https://gdq.se/sv/integritetspolicy/" target="_blank" rel="noopener">integritetspolicyn</a>'
        : '<a href="https://gdq.se/en/privacypolicy/" target="_blank" rel="noopener">privacy policy</a>';
      return fieldLabel(f).replace(/%s/g, privacy);
    }

    function fieldLiClass(f, ai, fi) {
      let cls;
      if (f.type === 'name') cls = 'mec-book-field-name mec-book-reg-field-name col-md-6';
      else if (f.type === 'mec_email') cls = 'mec-book-field-mec_email mec-book-reg-field-mec_email col-md-6';
      else if (f.type === 'text') cls = 'mec-book-field-text mec-book-reg-field-text col-md-12';
      else if (f.type === 'textarea') cls = 'mec-book-field-textarea mec-book-reg-field-textarea col-md-12';
      else if (f.type === 'radio') cls = 'mec-book-field-radio mec-book-reg-field-radio col-md-12';
      else if (f.type === 'checkbox') cls = 'mec-book-field-checkbox mec-book-reg-field-checkbox col-md-12';
      else if (f.type === 'agreement') cls = 'mec-book-field-agreement mec-book-reg-field-agreement col-md-12';
      else if (f.type === 'p') cls = 'mec-book-field-p mec-book-reg-field-p col-md-12';
      else cls = 'col-md-12';
      if (f.mandatory) cls += ' mec-reg-mandatory';
      if (err(ai, fi) || errLast(ai, fi)) cls += ' mec-red-notification';
      return cls;
    }

    function err(ai, fi) {
      return fieldErrs[ai + '-' + fi] || '';
    }

    function errLast(ai, fi) {
      return fieldErrs[ai + '-' + fi + '-1'] || '';
    }

    function clearErr(ai, fi) {
      delete fieldErrs[ai + '-' + fi];
    }

    function clearErrLast(ai, fi) {
      delete fieldErrs[ai + '-' + fi + '-1'];
    }

    function syncAttendees() {
      while (attendees.length > qty.value) attendees.pop();
      while (attendees.length < qty.value) {
        const d = {};
        fields.value.forEach((f, fi) => {
          if (f.type === 'agreement') d['f' + fi] = true;
          else if (f.type === 'checkbox') d['f' + fi] = [];
        });
        attendees.push({ first_name: '', last_name: '', email: '', data: d });
      }
    }

    function incQty() {
      const max = event.value.available || 0;
      if (qty.value < max) qty.value++;
      syncAttendees();
    }

    function decQty() {
      if (qty.value > 0) qty.value--;
      syncAttendees();
    }

    function scrollEl(id) {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    function goAttendees() {
      if (qty.value <= 0) return;
      syncAttendees();
      step.value = 2;
      scrollEl('gdq-book-form');
    }

    function goBackToStep1() {
      step.value = 1;
      scrollEl('gdq-book-card');
    }

    function validateAttendees() {
      Object.keys(fieldErrs).forEach(k => delete fieldErrs[k]);
      let bad = false;
      attendees.forEach((a, ai) => {
        fields.value.forEach((f, fi) => {
          if (f.type === 'p') return;
          if (f.type === 'name') {
            if (f.mandatory && !(a.first_name || '').trim()) {
              fieldErrs[ai + '-' + fi] = t(T.required);
              bad = true;
            }
            if (f.mandatory && !(a.last_name || '').trim()) {
              fieldErrs[ai + '-' + fi + '-1'] = t(T.required);
              bad = true;
            }
            return;
          }
          let v;
          if (f.type === 'mec_email') v = a.email;
          else v = a.data['f' + fi];
          if (f.type === 'agreement') {
            if (f.mandatory && v !== true) {
              fieldErrs[ai + '-' + fi] = t(T.required);
              bad = true;
            }
            return;
          }
          const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
          if (f.type === 'mec_email') {
            if (f.mandatory && empty) {
              fieldErrs[ai + '-' + fi] = t(T.required);
              bad = true;
            } else if (!empty && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v))) {
              fieldErrs[ai + '-' + fi] = t(T.email_invalid);
              bad = true;
            }
          } else if (f.mandatory && empty) {
            fieldErrs[ai + '-' + fi] = t(T.required);
            bad = true;
          }
        });
      });
      return !bad;
    }

    function addToCart() {
      if (!validateAttendees()) return;
      saving.value = true;
      error.value = '';
      const a0 = attendees[0];
      store.dispatch('event_booking_create', {
        event_id: Number(route.params.id),
        lang: lang.value,
        registrant_first_name: a0.first_name,
        registrant_last_name: a0.last_name,
        registrant_email: a0.email,
        attendees: attendees.map(a => {
          return { quantity: 1, first_name: a.first_name, last_name: a.last_name, email: a.email, data: a.data };
        })
      }).then(resp => {
        saving.value = false;
        if (resp.data.status === 'ok') {
          booking.value = resp.data;
          view.value = Number((event.value && event.value.price) || 0) > 0 ? 'checkout' : 'done';
          window.scrollTo({ top: 0 });
        } else {
          error.value = resp.data.error || t(T.booking_failed);
        }
      }).catch(e2 => {
        saving.value = false;
        const d = e2.response ? e2.response.data : null;
        error.value = (d && d.error) ? d.error : t(T.booking_failed);
      });
    }

    function completeBooking() {
      view.value = 'done';
      window.scrollTo({ top: 0 });
    }

    function scrollToGateway() {
      const el = document.getElementById('gdq-gateway-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }

    function scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onMounted(async () => {
      let eventResp;
      let textsResp;
      try {
        [eventResp, textsResp] = await Promise.all([
          store.dispatch('event_get', { id: Number(route.params.id), lang: lang.value }),
          store.dispatch('eventtexts_fetch', { lang: lang.value })
        ]);
      } catch (e2) {
        loading.value = false;
        return;
      }
      if (textsResp.data && typeof textsResp.data === 'object') Object.assign(texts.value, textsResp.data);
      loading.value = false;
      if (eventResp.data.status === 'ok') {
        event.value = eventResp.data.result;
        qty.value = 1;
        syncAttendees();
      }
    });

    return {
      ICONS,
      config,
      lang,
      t,
      T,
      loading,
      event,
      error,
      view,
      step,
      qty,
      attendees,
      saving,
      booking,
      ticketName,
      doneNote,
        currencyLabel,
        eventDates,
        priceTotal,
        fields,
      formatPrice,
      priceText,
      fieldLabel,
      optionText,
      agreementHtml,
      fieldLiClass,
      err,
      errLast,
      clearErr,
      clearErrLast,
      incQty,
      decQty,
      goAttendees,
      goBackToStep1,
      addToCart,
      completeBooking,
      scrollToGateway,
      scrollToTop
    };
  }
};
</script>

<style scoped>
.gdq-flow-col {max-width: 540px;}
.gdq-book-card {padding: 32px 36px 36px !important;
  background: #fff;
  border: 1px solid #e6e7e8;
  border-radius: var(--mec-primary-border-radius, 8px);
  margin-bottom: 30px;}
.gdq-book-heading {margin-bottom: 24px;}
.gdq-attendee-heading {margin-top: 20px;}
.gdq-attendee-heading h4 {font-size: 18px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef0f1;}
.gdq-book-rows {display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid #eef0f1;}
.gdq-book-row {display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 4px;
  border-bottom: 1px solid #eef0f1;}
.gdq-book-row-label {color: #606367;
  font-size: 14px;
  font-weight: 400;}
.gdq-book-row-value {font-size: 16px;
  font-weight: 600;
  color: #212224;}
.gdq-book-row-total .gdq-book-row-label {font-weight: 600;
  color: #212224;}
.gdq-book-row-total .gdq-book-row-value {font-size: 20px;
  color: #007d8f;}
.gdq-qty-stepper {display: flex;
  align-items: center;
  gap: 8px;}
.gdq-qty-btn {width: 38px !important;
  height: 38px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid #d9dcde !important;
  border-radius: 6px !important;
  background: #fff !important;
  box-shadow: none !important;
  color: #007d8f !important;
  font-size: 18px !important;
  line-height: 1 !important;
  text-transform: none !important;
  cursor: pointer;
  transition: background 0.15s ease;}
.gdq-qty-btn:hover:not(:disabled) {background: #e4f1f4;}
.gdq-qty-btn:disabled {color: #c4c8ca;
  cursor: default;}
.gdq-qty-input {width: 56px;
  height: 38px;
  border: 1px solid #d9dcde;
  border-radius: 6px;
  text-align: center;
  font-size: 15px;
  color: #212224;
  -moz-appearance: textfield;}
.gdq-qty-input::-webkit-outer-spin-button, .gdq-qty-input::-webkit-inner-spin-button {-webkit-appearance: none;
  margin: 0;}
.gdq-book-avail {display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 4px 0;
  font-size: 13px;
  color: #8e9195;}
.gdq-avail-dot {width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #47dc6e;
  flex-shrink: 0;}
.gdq-btn {display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 45px !important;
  padding: 0 20px !important;
  border-radius: 8px !important;
  font-size: 15px !important;
  line-height: 1 !important;
  text-transform: none !important;
  box-shadow: none !important;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  margin: 0 !important;
  transition: background 0.15s ease, border-color 0.15s ease;}
.gdq-btn:disabled {cursor: default;}
.gdq-btn.gdq-btn-primary {background: #007d8f !important;
  border: 1px solid #007d8f !important;
  color: #fff !important;}
.gdq-btn.gdq-btn-primary:hover:not(:disabled) {background: #00647a !important;
  border-color: #00647a !important;}
.gdq-btn.gdq-btn-primary:disabled {background: #b9dce2 !important;
  border-color: #b9dce2 !important;}
.gdq-btn.gdq-btn-secondary {background: #fff !important;
  border: 1px solid #007d8f !important;
  color: #007d8f !important;}
.gdq-btn.gdq-btn-secondary:hover {background: #e4f1f4 !important;}
.gdq-form-btns {display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;}
.gdq-form-btns .gdq-btn {width: 100%;
  margin: 0 !important;}
.gdq-flow-col > .mec-cart, .gdq-flow-col > .mec-checkout {border: 1px solid transparent;}
.gdq-flow-col .mec-cart-coupon-checkout-action {padding: 0 36px;}
.gdq-flow-col .mec-checkout-gateways-wrapper {max-width: 100%;
  border: none !important;}
.gdq-flow-col .mec-checkout-footer {padding: 30px 36px 15px;}
.gdq-flow-col ul.mec-book-tickets-reg-fields-container {margin: 0 !important;
  overflow: visible;}
.gdq-flow-col ul.mec-book-tickets-reg-fields-container > li {width: 100% !important;
  margin-right: 0 !important;
  margin-left: 0 !important;
  float: none;}
.gdq-btn-block {width: 100%;
  margin: 24px 0 0 !important;}
.gdq-confirm-body {padding: 40px 24px 24px;}
.gdq-confirm-check {width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background-color: #007d8f;
  color: #fff;
  font-size: 28px;
  line-height: 64px;
  text-align: center;}
.gdq-confirm-title {color: #007d8f;
  font-weight: 500;}
.mec-wrap {position: relative;
  font-family: var(--mec-paragraph-font-family);}
.mec-wrap h1, .mec-wrap h2, .mec-wrap h3, .mec-wrap h4, .mec-wrap h5, .mec-wrap h6, .entry-content .mec-wrap h1, .entry-content .mec-wrap h2, .entry-content .mec-wrap h3, .entry-content .mec-wrap h4, .entry-content .mec-wrap h5, .entry-content .mec-wrap h6 {font-family: var(--mec-heading-font-family);
  color: #171c24;
  font-weight: 400;
  font-style: inherit;
  letter-spacing: normal;
  clear: none;}
.mec-wrap h1 {font-size: 50px;
  line-height: 1.16;
  margin-bottom: 12px;
  letter-spacing: -1px;}
.mec-wrap h2 {font-size: 36px;
  line-height: 1.14;
  margin-bottom: 10px;}
.mec-wrap h3 {font-size: 28px;
  line-height: 1.2;
  margin-bottom: 8px;}
.mec-wrap h4 {font-size: 24px;
  line-height: 1.2;
  margin-bottom: 10px;}
.mec-wrap h5 {font-size: 18px;
  line-height: 1.3;
  margin-bottom: 7px;}
.mec-wrap h6 {font-size: 16px;
  line-height: 1.3;
  margin-bottom: 4px;}
.mec-wrap h1 strong {font-weight: 700;}
.mec-wrap p {margin: 0 0 20px 0;
  color: #616161;
  font-size: 14px;
  line-height: 1.8;}
.mec-wrap abbr, .mec-wrap acronym {cursor: auto;
  border: none;}
.entry-content .mec-wrap a {box-shadow: none;}
.mec-wrap .button, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove), .mec-wrap input[type="submit"], .mec-wrap input[type="reset"], .mec-wrap input[type="button"], .lity-content .button, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove), .lity-content input[type="submit"], .lity-content input[type="reset"], .lity-content input[type="button"], .lity-content input[type="submit"], .mec-nextprev-next-button, .mec-nextprev-prev-button {position: relative;
  border: 1px solid #ccecf4;
  border-radius: 4px;
  box-shadow: 0 1px 0 0 #fcfcfc;
  color: #07bbe9;
  display: inline-block;
  font-size: 14px;
  line-height: 18px;
  font-family: inherit;
  text-transform: capitalize;
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  margin-bottom: 20px;
  margin-right: 10px;
  padding: 9px 24px 9px;
  background: #ecf9fd;
  -webkit-transition: all 0.21s ease;
  -moz-transition: all 0.21s ease;
  transition: all 0.21s ease;
  border-radius: var(--mec-secondary-border-radius);}
.mec-wrap .button:hover, .mec-wrap a.button:hover, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove):hover, .mec-wrap input[type="submit"]:hover, .mec-wrap input[type="reset"]:hover, .mec-wrap input[type="button"]:hover, .lity-content .button:hover, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove):hover, .lity-content input[type="submit"]:hover, .lity-content input[type="reset"]:hover, .lity-content input[type="button"]:hover, .lity-content input[type="submit"]:hover {border: 1px solid #38d5ed;
  box-shadow: 0 1px 0 0 #fafafa;}
.mec-wrap .button svg, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove) svg, .mec-wrap input[type="submit"] svg, .mec-wrap input[type="reset"] svg, .mec-wrap input[type="button"] svg, .lity-content .button svg, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove) svg, .lity-content input[type="submit"] svg, .lity-content input[type="reset"] svg, .lity-content input[type="button"] svg, .mec-nextprev-next-button svg, .mec-nextprev-prev-button svg {margin-left: 25px;}
.mec-wrap .button svg.back, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.et-fb-button) svg.back {margin-right: 30px;
  margin-left: 0;}
.mec-booking-form-container button {display: block;}
.mec-wrap abbr {cursor: auto;
  border-bottom: 0;}
.mec-wrap {font-weight: 400;
  color: #626262;}
.mec-wrap .mec-container a {box-shadow: none;}
.mec-event-content p {font-weight: 400;}
.mec-events-button {background: #fff;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0;
  border: 1px solid #e3e3e3;
  border-radius: var(--mec-secondary-border-radius);
  text-shadow: none;
  margin-right: 10px;
  transition: 0.3s;}
.mec-single-event #mec-wrap {padding: 0;
  margin-top: 35px;}
.single-mec-events .mec-wrap.mec-no-access-error h1, .mec-wrap .mec-single-title {margin-top: 0;
  margin-bottom: 30px;
  font-weight: 700;
  font-size: 33px;}
.mec-single-event .mec-event-content {padding: 40px 0 30px;
  margin-bottom: 10px;}
.mec-single-event .mec-frontbox, .mec-single-event .mec-events-meta-group-booking, .mec-events-meta-group-booking .mec-booking-shortcode {margin-bottom: 30px;
  padding: 40px;
  background: #fff;
  border: 1px solid #e6e7e8;}
.mec-single-event .mec-frontbox, .lity-content .mec-events-meta-group-booking, .mec-single-event .mec-events-meta-group-booking, .mec-events-meta-group-booking .mec-booking-shortcode, .mec-single-event .mec-events-meta-group-countdown, .mec-single-event .mec-events-meta-group-gmap, .mec-single-modern .mec-single-event-bar {border-radius: var(--mec-primary-border-radius);
  overflow: hidden;
  margin-bottom: 30px;}
.mec-wrap #main-content {overflow: hidden;
  padding-top: 35px;}
.mec-events-meta-group-tags {margin-top: 20px;}
.mec-events-meta-group-tags a {display: inline-block;
  color: #444;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 500;
  padding: 3px 7px;
  border: 1px solid #ddd;
  border-radius: var(--mec-secondary-border-radius);
  background: #fff;
  margin: 1px 3px;}
.mec-events-meta-group-tags a:hover {text-decoration: underline;
  background: #f9f9f9;}
.lity-content .mec-events-meta-group-booking, .mec-single-event .mec-events-meta-group-booking, .mec-events-meta-group-booking .mec-booking-shortcode {z-index: 1;
  overflow: visible;}
.lity-content .mec-events-meta-group-booking ul.mec-book-tickets-reg-fields-container, .mec-single-event .mec-events-meta-group-booking ul.mec-book-tickets-reg-fields-container, .mec-events-meta-group-booking .mec-booking-shortcode ul.mec-book-tickets-reg-fields-container, .lity-content .mec-events-meta-group-booking .mec-paypal-credit-card-payment-fields, .mec-single-event .mec-events-meta-group-booking .mec-paypal-credit-card-payment-fields, .mec-events-meta-group-booking .mec-booking-shortcode .mec-paypal-credit-card-payment-fields {margin: 0px -41.835px;
  overflow: hidden;
  clear: both;}
.lity-content .mec-events-meta-group-booking ul, .mec-single-event .mec-events-meta-group-booking ul, .mec-events-meta-group-booking .mec-booking-shortcode ul {list-style: none;
  padding-left: 0;
  margin-left: 0;}
.lity-content .mec-events-meta-group-booking ul li, .mec-single-event .mec-events-meta-group-booking ul li, .mec-single-event .mec-events-meta-group-booking .mec-book-ticket-variation-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode ul li:not(.option), .mec-events-meta-group-booking .mec-booking-shortcode .mec-book-ticket-variation-wrapper {padding: 0;
  list-style: none;
  margin-top: 0;
  margin-bottom: 30px;}
.lity-content .mec-events-meta-group-booking ul li.col-md-6:first-child, .mec-single-event .mec-events-meta-group-booking ul li.col-md-6:first-child {width: calc(50% - 10px);
  margin-right: 10px;
  margin-left: 0;}
.lity-content .mec-events-meta-group-booking ul li.col-md-6:nth-child(2), .mec-single-event .mec-events-meta-group-booking ul li.col-md-6:nth-child(2) {width: calc(50% - 10px);
  margin-right: 0;
  margin-left: 10px;}
.lity-content .mec-events-meta-group-booking .col-md-12 .mec-ticket-icon-wrapper, .mec-single-event .mec-events-meta-group-booking .col-md-12 .mec-ticket-icon-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode .col-md-12 .mec-ticket-icon-wrapper {margin-right: 25px;}
.lity-content .mec-events-meta-group-booking .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span, .mec-single-event .mec-events-meta-group-booking .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span, .mec-events-meta-group-booking .mec-booking-shortcode .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span {display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  color: #606367;
  margin-bottom: 4px;}
.lity-content .mec-events-meta-group-booking .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span.mec-ticket-price, .mec-single-event .mec-events-meta-group-booking .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span.mec-ticket-price, .mec-events-meta-group-booking .mec-booking-shortcode .col-md-12 .mec-ticket-name-description-wrapper .mec-ticket-price-wrapper span.mec-ticket-price {font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #212224;
  margin: 0;}
.lity-content .mec-events-meta-group-booking [id*="mec_booking_quantity_wrapper_"], .mec-single-event .mec-events-meta-group-booking [id*="mec_booking_quantity_wrapper_"], .mec-events-meta-group-booking .mec-booking-shortcode [id*="mec_booking_quantity_wrapper_"] {font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: #8e9195;
  display: block;
  margin-bottom: 5px;
  text-align: end;}
.lity-content .mec-events-meta-group-booking li h4, .mec-single-event .mec-events-meta-group-booking li h4, .mec-events-meta-group-booking .mec-booking-shortcode li h4 {font-size: 19px;}
.lity-content .mec-events-meta-group-booking input, .mec-single-event .mec-events-meta-group-booking input, .mec-events-meta-group-booking .mec-booking-shortcode input, .lity-content .mec-events-meta-group-booking button, .mec-single-event .mec-events-meta-group-booking button, .mec-events-meta-group-booking .mec-booking-shortcode button {border-radius: 0;
  margin-bottom: 6px;}
.lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-2, .lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-3, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-3, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-3 {color: #636f72;
  background: #f6f7f8;
  border: 1px solid #e7e8e9;
  float: left;
  margin: 0;}
.lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2:hover, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2:hover, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-2:hover, .lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2:hover, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2:hover, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-3:hover {border: 1px solid #d9dadc;}
.lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2 img.mec-svg-icon, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2 img.mec-svg-icon, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-2 img.mec-svg-icon, .lity-content .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-3 img.mec-svg-icon, .mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-3 img.mec-svg-icon, .mec-events-meta-group-booking .mec-booking-shortcode button.mec-book-form-back-button#mec-book-form-back-btn-step-3 img.mec-svg-icon, .mec-nextprev-prev-button svg {margin-left: unset;
  margin-right: 25px;}
.lity-content .mec-events-meta-group-booking .mec-click-pay, .mec-single-event .mec-events-meta-group-booking .mec-click-pay, .mec-events-meta-group-booking .mec-booking-shortcode .mec-click-pay {max-width: 100%;}
.lity-content .mec-events-meta-group-booking .mec-click-pay #mec_woo_add_to_cart_btn_r, .mec-single-event .mec-events-meta-group-booking .mec-click-pay #mec_woo_add_to_cart_btn_r, .mec-events-meta-group-booking .mec-booking-shortcode .mec-click-pay #mec_woo_add_to_cart_btn_r, .lity-content .mec-events-meta-group-booking .mec-click-pay button.mec-book-form-next-button, .mec-single-event .mec-events-meta-group-booking .mec-click-pay button.mec-book-form-next-button, .mec-events-meta-group-booking .mec-booking-shortcode .mec-click-pay button.mec-book-form-next-button {float: right;
  margin: 30px 0 0 0 !important;
  background: #47dc6e !important;
  border-color: #47dc6e !important;
  color: #fff !important;
  font-weight: 700 !important;}
.lity-content .mec-events-meta-group-booking .mec-click-pay #mec_woo_add_to_cart_btn_r:hover, .mec-single-event .mec-events-meta-group-booking .mec-click-pay #mec_woo_add_to_cart_btn_r:hover, .mec-events-meta-group-booking .mec-booking-shortcode .mec-click-pay #mec_woo_add_to_cart_btn_r:hover, .lity-content .mec-events-meta-group-booking .mec-click-pay button.mec-book-form-next-button:hover, .mec-single-event .mec-events-meta-group-booking .mec-click-pay button.mec-book-form-next-button:hover, .mec-events-meta-group-booking .mec-booking-shortcode .mec-click-pay button.mec-book-form-next-button:hover {background: #54d274;}
.mec-booking .mec-event-tickets-list .mec-event-ticket-available {margin-top: 12px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row input[type="number"] {display: block;
  margin: 0 0 10px auto;
  box-shadow: unset;
  border: 1px solid #e3e5e7;
  border-radius: var(--mec-secondary-border-radius);
  background: #fff;
  min-width: 80px;
  width: 80px !important;
  min-height: 40px;
  height: 40px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row input::-webkit-outer-spin-button, .mec-booking .mec-event-tickets-list .mec-ticket-style-row input::-webkit-inner-spin-button {-webkit-appearance: none;
  margin: 0;}
.mec-ticket-style-row.mec-ticket-available-spots {display: flex; flex-direction: row; align-items: center; justify-content: flex-start; align-content: stretch; flex-wrap: nowrap;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-ticket-style-row-section-3 {margin-left: auto;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-input-wrapper {position: relative;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row input[type="number"] {-moz-appearance: textfield;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .plus, .mec-booking .mec-event-tickets-list .mec-ticket-style-row .minus {position: absolute;
  right: 5px;
  line-height: 1;
  height: 14px;
  display: flex;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .plus {top: 5px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .minus {top: auto;
  bottom: 5px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row a {text-decoration: none;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row {margin-bottom: 30px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-ticket-style-row-section-1 {width: unset;
  margin: 5px 25px 0 0;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-available, .mec-booking .mec-event-tickets-list .mec-ticket-style-row>div {border: 0;
  display: block;
  box-shadow: unset;
  margin: 0;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-available {font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: #8e9195;
  text-align: right;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-name {font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  color: #606367;
  margin-bottom: 4px;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-price {font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #212224;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-ticket-style-row-section-3 div, .mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-description, .mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-price {margin: 0;}
.mec-single-event .mec-book-form-price, .mec-single-event .mec-book-form-gateways, .mec-single-event form.mec-click-next, .mec-single-event .mec-book-first, .mec-single-event .mec-event-tickets-list {padding-left: 0;
  padding-right: 0;}
.mec-single-event label.mec-fill-attendees {margin-left: 0;}
.lity-content .mec-booking .mec-book-form-btn-wrap, .mec-wrap .mec-booking .mec-book-form-btn-wrap {padding-top: 30px;
  border-top: 1px solid #f0f1f2;}
.mec-events-meta-group-booking #mec-book-form-btn-step-1, .mec-events-meta-group-booking #mec-book-form-btn-step-2, .mec-events-meta-group-booking #mec-book-form-btn-step-3 {margin: 0;
  float: right;}
.mec-wrap .mec-booking-form-container .col-md-12 {padding-left: 0;}
.lity-content .mec-events-meta-group-booking .mec-event-ticket-available, .mec-single-event .mec-events-meta-group-booking .mec-event-ticket-available, .mec-events-meta-group-booking .mec-booking-shortcode .mec-event-ticket-available {display: block;
  margin-bottom: 20px;
  margin-top: -17px;
  font-size: 11px;
  color: #8a8a8a;}
.lity-content .mec-events-meta-group-booking label, .mec-single-event .mec-events-meta-group-booking label, .mec-events-meta-group-booking .mec-booking-shortcode label, .lity-content .mec-events-meta-group-booking h5, .mec-single-event .mec-events-meta-group-booking h5, .mec-events-meta-group-booking .mec-booking-shortcode h5 {color: #606367;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0;
  margin: 0 0 4px 0;
  display: block;
  clear: none;
  padding: 0 1em 5px 0;}
.lity-content .mec-events-meta-group-booking h5 span, .mec-single-event .mec-events-meta-group-booking h5 span, .mec-events-meta-group-booking .mec-booking-shortcode h5 span {display: inline-block;}
.lity-content .mec-events-meta-group-booking h5 span.mec-ticket-variation-name, .mec-single-event .mec-events-meta-group-booking h5 span.mec-ticket-variation-name, .mec-events-meta-group-booking .mec-booking-shortcode h5 span.mec-ticket-variation-name {padding-right: 5px;
  text-transform: capitalize;}
.lity-content .mec-events-meta-group-booking input::-webkit-input-placeholder, .mec-single-event .mec-events-meta-group-booking input::-webkit-input-placeholder, .mec-events-meta-group-booking .mec-booking-shortcode input::-webkit-input-placeholder {color: #aaa;}
.lity-content .mec-events-meta-group-booking input:-moz-placeholder, .mec-single-event .mec-events-meta-group-booking input:-moz-placeholder, .mec-events-meta-group-booking .mec-booking-shortcode input:-moz-placeholder {color: #aaa;}
.lity-content .mec-events-meta-group-booking input[type="text"], .mec-single-event .mec-events-meta-group-booking input[type="text"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="text"], .lity-content .mec-events-meta-group-booking input[type="date"], .mec-single-event .mec-events-meta-group-booking input[type="date"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="date"], .lity-content .mec-events-meta-group-booking input[type="number"], .mec-single-event .mec-events-meta-group-booking input[type="number"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="number"], .lity-content .mec-events-meta-group-booking input[type="email"], .mec-single-event .mec-events-meta-group-booking input[type="email"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="email"], .lity-content .mec-events-meta-group-booking input[type="password"], .mec-single-event .mec-events-meta-group-booking input[type="password"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="password"], .lity-content .mec-events-meta-group-booking input[type="tel"], .mec-single-event .mec-events-meta-group-booking input[type="tel"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="tel"], .lity-content .mec-events-meta-group-booking textarea, .mec-single-event .mec-events-meta-group-booking textarea, .mec-events-meta-group-booking .mec-booking-shortcode textarea, .lity-content .mec-events-meta-group-booking select, .mec-single-event .mec-events-meta-group-booking select, .mec-events-meta-group-booking .mec-booking-shortcode select {display: block;
  background: #fff;
  min-height: 40px;
  min-width: 180px;
  font-size: 13px;
  line-height: 38px;
  color: #606367;
  border: 1px solid #e3e5e7;
  border-radius: var(--mec-secondary-border-radius);
  padding: 0 12px;
  width: 100%;
  margin-bottom: 0;
  box-shadow: unset;
  clear: both;
  font-family: inherit;}
.lity-content .mec-events-meta-group-booking select:not(#mec_paypal_credit_card_card_type), .mec-single-event .mec-events-meta-group-booking select:not(#mec_paypal_credit_card_card_type), .mec-events-meta-group-booking .mec-booking-shortcode select:not(#mec_paypal_credit_card_card_type) {width: 100%;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMS40MjQiIGhlaWdodD0iNi40MiIgdmlld0JveD0iMCAwIDExLjQyNCA2LjQyIj4NCiAgPGcgaWQ9Ikdyb3VwXzY1NjI3IiBkYXRhLW5hbWU9Ikdyb3VwIDY1NjI3IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwLjIxMiAwLjIxMikiPg0KICAgIDxwYXRoIGlkPSJhbmdsZS1sZWZ0IiBkPSJNLjk5LDUuNWw1LDUuMDA1LS41LjVMMCw1LjUsNS41LDAsNiwuNS45OSw1LjVaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwIDUuOTk1KSByb3RhdGUoLTkwKSIgZmlsbD0iIzYzNmY3MiIgc3Ryb2tlPSIjNjM2ZjcyIiBzdHJva2Utd2lkdGg9IjAuMyIvPg0KICA8L2c+DQo8L3N2Zz4NCg==");
  background-position: calc(100% - 12px) center;
  background-repeat: no-repeat;
  border-radius: 0 var(--mec-secondary-border-radius) var(--mec-secondary-border-radius) 0;}
.wbmec-mandatory {padding-left: 5px;
  font-size: 14px;
  color: red;}
.lity-content .mec-events-meta-group-booking .mec-booking-email-field-wrapper, .mec-single-event .mec-events-meta-group-booking .mec-booking-email-field-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-email-field-wrapper, .lity-content .mec-events-meta-group-booking .mec-booking-name-field-wrapper, .mec-single-event .mec-events-meta-group-booking .mec-booking-name-field-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-name-field-wrapper, .lity-content .mec-events-meta-group-booking .mec-stripe-name-and-email-wrapper, .mec-single-event .mec-events-meta-group-booking .mec-stripe-name-and-email-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode .mec-stripe-name-and-email-wrapper, .lity-content .mec-events-meta-group-booking .mec-field-wrapper, .mec-single-event .mec-events-meta-group-booking .mec-field-wrapper, .mec-events-meta-group-booking .mec-booking-shortcode .mec-field-wrapper {display: flex;
  align-items: flex-start;}
.lity-content .mec-events-meta-group-booking .mec-booking-email-field-wrapper span.mec-booking-email-field-icon, .mec-single-event .mec-events-meta-group-booking .mec-booking-email-field-wrapper span.mec-booking-email-field-icon, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-email-field-wrapper span.mec-booking-email-field-icon, .lity-content .mec-events-meta-group-booking .mec-booking-name-field-wrapper span.mec-booking-name-field-icon, .mec-single-event .mec-events-meta-group-booking .mec-booking-name-field-wrapper span.mec-booking-name-field-icon, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-name-field-wrapper span.mec-booking-name-field-icon, .lity-content .mec-events-meta-group-booking .mec-stripe-name-and-email-wrapper span.mec-booking-email-field-icon, .mec-single-event .mec-events-meta-group-booking .mec-stripe-name-and-email-wrapper span.mec-booking-email-field-icon, .mec-events-meta-group-booking .mec-booking-shortcode .mec-stripe-name-and-email-wrapper span.mec-booking-email-field-icon, .lity-content .mec-events-meta-group-booking .mec-field-wrapper span.mec-field-icon, .mec-single-event .mec-events-meta-group-booking .mec-field-wrapper span.mec-field-icon, .mec-events-meta-group-booking .mec-booking-shortcode .mec-field-wrapper span.mec-field-icon {border: 1px solid #e3e5e7;
  background: #fafafa;
  border-radius: var(--mec-secondary-border-radius) 0 0 var(--mec-secondary-border-radius);
  min-width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;}
.lity-content .mec-events-meta-group-booking .mec-booking-name-field-wrapper input, .mec-single-event .mec-events-meta-group-booking .mec-booking-name-field-wrapper input, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-name-field-wrapper input, .lity-content .mec-events-meta-group-booking .mec-field-wrapper input, .mec-single-event .mec-events-meta-group-booking .mec-field-wrapper input, .mec-events-meta-group-booking .mec-booking-shortcode .mec-field-wrapper input {border-left: none;
  border-radius: 0 var(--mec-secondary-border-radius) var(--mec-secondary-border-radius) 0;}
.lity-content .mec-events-meta-group-booking input[type="text"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="text"]:focus, .mec-events-meta-group-booking .mec-booking-shortcode input[type="text"]:focus, .lity-content .mec-events-meta-group-booking input[type="number"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="number"]:focus, .mec-events-meta-group-booking .mec-booking-shortcodec, .lity-content .mec-events-meta-group-booking input[type="number"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="number"]:focus, .mec-events-meta-group-booking .mec-booking-shortcode input[type="number"]:focus, .lity-content .mec-events-meta-group-booking input[type="email"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="email"]:focus, .mec-events-meta-group-booking .mec-booking-shortcode input[type="email"]:focus, .lity-content .mec-events-meta-group-booking input[type="password"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="password"]:focus, .mec-events-meta-group-booking .mec-booking-shortcode input[type="password"]:focus, .lity-content .mec-events-meta-group-booking input[type="tel"]:focus, .mec-single-event .mec-events-meta-group-booking input[type="tel"]:focus, .mec-events-meta-group-booking .mec-booking-shortcode input[type="tel"]:focus, .lity-content .mec-events-meta-group-booking textarea:focus, .mec-single-event .mec-events-meta-group-booking textarea:focus, .mec-events-meta-group-booking .mec-booking-shortcode textarea:focus, .lity-content .mec-events-meta-group-booking select:focus, .mec-single-event .mec-events-meta-group-booking select:focus, .mec-events-meta-group-booking .mec-booking-shortcode select:focus {outline: none;}
.lity-content .mec-events-meta-group-booking input[type="checkbox"], .mec-single-event .mec-events-meta-group-booking input[type="checkbox"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"], .lity-content .mec-events-meta-group-booking input[type="radio"], .mec-single-event .mec-events-meta-group-booking input[type="radio"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="radio"] {margin-right: 6px;
  margin-top: 5px;
  min-height: 20px;
  clear: none;
  margin: 0;}
.lity-content .mec-events-meta-group-booking input[type="radio"]:before, .mec-single-event .mec-events-meta-group-booking input[type="radio"]:before, .mec-events-meta-group-booking .mec-booking-shortcode input[type="radio"]:before, .lity-container .mec-events-meta-group-booking input[type="radio"]:before {content: "";
  display: inline-block;
  background: #fff;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  cursor: pointer;
  border: 2px solid #e1e7ed;
  position: absolute;
  top: -2px;
  left: -2px;}
.mec-single-event .mec-events-meta-group-booking input[type="radio"]:checked:before, .mec-events-meta-group-booking .mec-booking-shortcode input[type="radio"]:checked:before, .lity-container .mec-events-meta-group-booking input[type="radio"]:checked:before {background: #38d5ed;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px #afebf5 !important;
  width: 14px;
  height: 14px;
  top: 0px;
  left: 0px;}
.lity-content .mec-events-meta-group-booking input[type="radio"], .mec-single-event .mec-events-meta-group-booking input[type="radio"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="radio"], .lity-container .mec-events-meta-group-booking input[type="radio"] {min-height: 0;
  margin: 0;
  position: relative;
  top: 0px;
  left: 3px;
  width: 14px;
  height: 14px;}
.lity-content .mec-events-meta-group-booking input[type="checkbox"], .mec-single-event .mec-events-meta-group-booking input[type="checkbox"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"] {float: left;}
.lity-content .mec-events-meta-group-booking .mec_book_first_for_all, .mec-single-event .mec-events-meta-group-booking .mec_book_first_for_all, .mec-events-meta-group-booking .mec-booking-shortcode .mec_book_first_for_all, .lity-container .mec-events-meta-group-booking .mec_book_first_for_all, .lity-content .mec-events-meta-group-booking input[type="checkbox"], .mec-single-event .mec-events-meta-group-booking input[type="checkbox"], .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"], .mec-single-event .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"], .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-dates-checkboxes input[type="checkbox"], .lity-content .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"], .mec-booking input[type="checkbox"], .mec-wrap .mec-simple-checkboxes-search ul li label input[type="checkbox"] {-webkit-appearance: none;
  appearance: none;
  font: inherit;
  width: 20px;
  height: 20px;
  min-height: unset;
  padding: 0;
  border: 1px solid #e3e5e7;
  border-radius: 4px;
  display: flex;
  margin: 0 10px 0 1px;
  overflow: hidden;}
.lity-content .mec-events-meta-group-booking .mec_book_first_for_all:before, .mec-single-event .mec-events-meta-group-booking .mec_book_first_for_all:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec_book_first_for_all:before, .lity-container .mec-events-meta-group-booking .mec_book_first_for_all:before, .lity-content .mec-events-meta-group-booking input[type="checkbox"]:before, .mec-single-event .mec-events-meta-group-booking input[type="checkbox"]:before, .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"]:before, .mec-single-event .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-dates-checkboxes input[type="checkbox"]:before, .lity-content .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:before, .mec-booking input[type="checkbox"]:before, .mec-wrap .mec-simple-checkboxes-search ul li label input[type="checkbox"]:before {content: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgMTAgOCI+DQogIDxwYXRoIGlkPSJEb25lIiBkPSJNOS4xNzUsMGEuOC44LDAsMCwwLS42NjUuMzA2bC01LjIsNS42NDdMMS4zODgsMy44MzNhLjcuNywwLDAsMC0uNjczLS4yMTIsMS4zODIsMS4zODIsMCwwLDAtLjcuNy44NjIuODYyLDAsMCwwLC4yMjYuNzI0TDIuNzk1LDcuNzkxYS43OTIuNzkyLDAsMCwwLDEuMDYyLjAxTDkuNzY5LDEuMzI5QS44MzIuODMyLDAsMCwwLDkuOTYuNTIxLDEsMSwwLDAsMCw5LjE3NSwwWiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMCAwKSIgZmlsbD0iI2ZmZiIvPg0KPC9zdmc+DQo=");
  min-width: 18px;
  height: auto;
  line-height: 16px;
  border-radius: 2px;
  transform: scale(0);
  transition: 120ms transform ease-in-out;
  background-color: var(--mec-color-skin);
  display: flex;
  justify-content: center;}
.mec-single-event .mec-events-meta-group-booking .mec_book_first_for_all:checked, .mec-events-meta-group-booking .mec-booking-shortcode .mec_book_first_for_all:checked, .lity-container .mec-events-meta-group-booking .mec_book_first_for_all:checked, .lity-content .mec-events-meta-group-booking input[type="checkbox"]:checked, .mec-single-event .mec-events-meta-group-booking input[type="checkbox"]:checked, .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"]:checked, .mec-single-event .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:checked, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-dates-checkboxes input[type="checkbox"]:checked, .lity-content .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:checked, .mec-booking input[type="checkbox"]:checked, .mec-wrap .mec-simple-checkboxes-search ul li label input[type="checkbox"]:checked {border: 1px solid var(--mec-color-skin) !important;}
.mec-single-event .mec-events-meta-group-booking .mec_book_first_for_all:checked:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec_book_first_for_all:checked:before, .lity-container .mec-events-meta-group-booking .mec_book_first_for_all:checked:before, .mec-single-event .mec-events-meta-group-booking input[type="checkbox"]:checked:before, .mec-events-meta-group-booking .mec-booking-shortcode input[type="checkbox"]:checked:before, .mec-single-event .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:checked:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec-booking-dates-checkboxes input[type="checkbox"]:checked:before, .lity-content .mec-events-meta-group-booking .mec-booking-dates-checkboxes input[type="checkbox"]:checked:before, .mec-booking input[type="checkbox"]:checked:before, .mec-wrap .mec-simple-checkboxes-search ul li label input[type="checkbox"]:checked:before {transform: scale(1);}
.lity-content .mec-events-meta-group-booking label.wn-checkbox-label, .mec-single-event .mec-events-meta-group-booking label.wn-checkbox-label, .mec-events-meta-group-booking .mec-booking-shortcode label.wn-checkbox-label, .lity-container .mec-events-meta-group-booking label.wn-checkbox-label {display: none;}
.mec-single-event a.button:after, .lity-content .mec-events-meta-group-booking button[type="submit"]:after, .mec-single-event .mec-events-meta-group-booking button[type="submit"]:after, .mec-events-meta-group-booking .mec-booking-shortcode button[type="submit"]:after, .mec-wrap .mec-events-meta-group-booking button[type="submit"]:after {display: none;
  font-family: "simple-line-icons";
  content: "\e098";
  margin-left: 4px;
  -webkit-animation: rotating2 1.2s linear infinite;
  -moz-animation: rotating2 1.2s linear infinite;
  -ms-animation: rotating2 1.2s linear infinite;
  -o-animation: rotating2 1.2s linear infinite;
  animation: rotating2 1.2s linear infinite;}
.mec-single-event a.button.loading:after, .lity-content .mec-events-meta-group-booking button[type="submit"].loading:after, .mec-single-event .mec-events-meta-group-booking button[type="submit"].loading:after, .mec-events-meta-group-booking .mec-booking-shortcode button[type="submit"].loading:after, .mec-wrap .mec-events-meta-group-booking button[type="submit"].loading:after {display: inline-block;}
.mec-single-event .mec-event-export-module {display: block;}
.mec-ticket-price {margin-left: 10px;
  font-size: 13px;
  font-weight: 400;}
.mec-book-reg-field-checkbox label, .mec-book-reg-field-radio label {line-height: 1.36;}
.mec-book-reg-field-radio label {display: inline-block !important;
  margin-bottom: 20px !important;}
.mec-book-reg-field-checkbox input[type="checkbox"] {float: left;
  margin-right: 10px !important;}
.mec-book-reg-field-radio input[type="radio"] {float: left;
  margin-right: 12px !important;}
.mec-book-ticket-container .wbmec-mandatory, .mec-book-ticket-container .mec-reg-mandatory:nth-child(2) label:after, .mec-book-ticket-container .mec-reg-mandatory:nth-child(3) label:after {content: "";
  color: red;
  width: 50px;
  height: 50px;
  font-size: 14px;
  padding-left: 5px;}
.mec-single-modern .col-md-4 .mec-sponsors-details h3, .mec-sponsors-details .mec-events-single-section-title, .mec-single-event .mec-event-meta h3, .mec-single-event .mec-event-meta dt, .mec-single-event .mec-event-meta .mec-cost, .mec-single-event .mec-event-meta .mec-date, .mec-single-event .mec-event-meta .mec-time, .mec-single-event .mec-event-meta .mec-events-single-section-title {text-transform: uppercase;
  font-size: 16px;
  font-weight: bold;
  padding-bottom: 5px;
  display: inline;
  color: #000;
  padding-left: 10px;}
.mec-single-event-sponsor span, .mec-single-event .mec-event-meta h6, .mec-single-event .mec-event-meta .mec-meta-label {font-size: 14px;
  padding-bottom: 5px;
  display: inline-block;
  color: #8d8d8d;
  margin: 0;
  padding: 0;}
.mec-single-event .mec-event-meta dd, .mec-single-event .mec-event-meta .mec-events-event-categories a {font-size: 14px;
  color: #8d8d8d;}
.mec-single-event .mec-event-meta dd {margin: 0;
  padding-left: 35px;}
.mec-single-event .mec-event-meta dd a {color: #8d8d8d;
  transition: all 0.2s ease;
  -ms-word-wrap: break-word;
  word-wrap: break-word;}
.mec-single-event .mec-event-meta dd a i:before {font-size: 16px !important;}
.mec-single-event .mec-event-meta dd a i {margin-right: 8px;}
.mec-single-event .mec-event-meta dl {margin-bottom: 0;}
.mec-single-event .mec-events-event-image {margin-bottom: 0;}
.mec-single-event .mec-events-event-image img {border-radius: var(--mec-primary-border-radius);
  max-width: 100%;
  height: auto;}
.mec-single-event h2.mec-single-event-title {margin-bottom: 30px;
  font-weight: 700;
  font-size: 33px;}
.mec-single-event .mec-booking-button {border-bottom: none;
  letter-spacing: 0.5px;
  line-height: 48px;
  height: 76px;
  transition: all 0.5s ease;
  color: #fff !important;
  padding: 16px;
  display: block;
  text-align: center;
  font-size: 16px;
  border-radius: var(--mec-secondary-border-radius);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  cursor: pointer !important;}
.lity-content .mec-events-meta-group-booking form, .mec-single-event .mec-events-meta-group-booking form, .mec-events-meta-group-booking .mec-booking-shortcode form, .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout {margin: 0;}
.mec-single-event .mec-frontbox-title, .mec-single-event .mec-wrap-checkout h4, .lity-content .mec-events-meta-group-booking .mec-wrap-checkout h4, .mec-single-event .mec-events-meta-group-booking .mec-wrap-checkout h4, .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout h4, .lity-content .mec-events-meta-group-booking form>h4, .mec-single-event .mec-events-meta-group-booking form>h4, .mec-events-meta-group-booking .mec-booking-shortcode form>h4 {text-transform: uppercase;
  font-size: 15px;
  font-weight: bold;
  color: #313131;
  border-bottom: 4px solid #ebebeb;
  width: 100%;
  display: block;
  padding-bottom: 10px;
  position: relative;
  text-align: center;
  margin: 0 0 40px 0;}
.mec-single-event .mec-frontbox-title:before, .mec-single-event .mec-wrap-checkout h4:before, .lity-content .mec-events-meta-group-booking .mec-wrap-checkout h4:before, .mec-single-event .mec-events-meta-group-booking .mec-wrap-checkout h4:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout h4:before, .lity-content .mec-events-meta-group-booking form>h4:before, .mec-single-event .mec-events-meta-group-booking form>h4:before, .mec-events-meta-group-booking .mec-booking-shortcode form>h4:before {padding: 1px 35px;
  border-bottom: 4px solid #40d9f1;
  font-size: 6px;
  content: "";
  text-align: center;
  position: absolute;
  bottom: -4px;
  margin-left: -35px;
  left: 50%;}
.lity-content .mec-events-meta-group-booking input[data-stripe="exp-month"], .mec-single-event .mec-events-meta-group-booking input[data-stripe="exp-month"], .mec-events-meta-group-booking .mec-booking-shortcode input[data-stripe="exp-month"], .lity-content .mec-events-meta-group-booking input[data-stripe="exp-year"], .mec-single-event .mec-events-meta-group-booking input[data-stripe="exp-year"], .mec-events-meta-group-booking .mec-booking-shortcode input[data-stripe="exp-year"] {width: 47% !important;
  margin-right: 12px;
  margin-top: 5px;
  display: inline-block !important;}
.mec-event-meta i:before {font-size: 20px !important;
  vertical-align: middle;}
.mec-event-meta .mec-single-event-organizer dl dd i:before, .mec-event-meta .mec-single-event-additional-organizers dl dd i:before, .mec-event-meta .mec-single-event-location dl dd i:before {font-size: 14px !important;}
#mec-events-content .mec-events-abbr {color: #8d8d8d;
  font-size: 14px;}
.mec-single-event .mec-events-content {margin-bottom: 30px;}
.mec-single-event #headline {margin: 0 0 10px;}
.mec-single-event #headline h2 {padding: 0;}
.mec-single-event .mec-event-meta .mec-single-event-organizer .mec-events-single-section-title, .mec-single-event .mec-event-meta .mec-single-event-additional-organizers .mec-events-single-section-title, .mec-single-event .mec-events-meta-date h3, .mec-single-event .mec-events-meta-date dt, .mec-single-event .mec-events-meta-date .mec-date, .mec-single-event .mec-events-meta-date .mec-time, .mec-single-event .mec-event-meta .mec-single-event-location .mec-events-single-section-title, .mec-single-event .mec-event-meta .mec-single-event-additional-locations .mec-events-single-section-title, .mec-single-event .mec-event-meta .mec-single-event-category .mec-events-single-section-title {padding: 0 0 5px 10px;
  margin-top: 0;
  display: inline-block;}
.mec-events-single-section-title a span {font-size: 12px;}
.mec-single-event .mec-events-event-image {border: 0;}
.mec-single-event-date, .mec-single-event-time, .mec-single-event-location, .mec-single-event-additional-locations, .mec-single-event-category, .mec-single-event-sponsor, .mec-single-event-label, .mec-single-event-sponsor, .mec-single-event-organizer, .mec-single-event-additional-organizers, .mec-events-meta-date, .mec-event-cost, .mec-event-website, .mec-event-more-info, .mec-local-time-details {padding: 12px 14px 8px;
  margin-bottom: 12px;
  vertical-align: baseline;
  position: relative;}
.mec-single-event .mec-single-event-organizer dd, .mec-single-event .mec-single-event-additional-organizers dd, .mec-single-event .mec-events-meta-date dd {padding-left: 35px;
  margin-bottom: 10px;
  display: table;}
.mec-single-event .mec-event-meta dd.mec-organizer-email a, .mec-single-event .mec-single-event-organizer dd span, .mec-single-event .mec-single-event-additional-organizers dd span, .mec-single-event .mec-events-meta-date dd span, .mec-single-event .mec-single-event-location dd span, .mec-single-event .mec-event-meta dd.mec-organizer-tel a {display: block;
  padding-left: 24px;
  color: #8d8d8d;}
.mec-single-event .mec-events-meta-date i, .mec-single-event .mec-single-event-location dl dd i, .mec-single-event .mec-single-event-organizer dl dd i, .mec-single-event .mec-single-event-additional-organizers dl dd i {margin-right: 10px;}
.mec-single-event .mec-event-content dt {margin-top: 5px;}
.mec-sponsor img, .mec-single-event-location img, .mec-single-event-organizer img, .mec-single-event-additional-organizers img {margin-bottom: 5px;
  width: 100%;
  border-radius: var(--mec-secondary-border-radius);
  padding-left: 35px;}
.mec-time-comment {font-size: 11px;}
.info-msg, .mec-success, .warning-msg, .mec-error {margin: 30px 0 20px 0;
  padding: 10px;
  border-radius: var(--mec-secondary-border-radius);
  font-size: 13px;
  line-height: 22px;}
.info-msg {color: #07bbe9;
  background-color: #ecf9fe;}
.lity-content .mec-events-meta-group-booking input[type="date"]::-webkit-calendar-picker-indicator, .lity-content #mec_fes_form input[type="date"]::-webkit-calendar-picker-indicator, .mec-single-event .mec-events-meta-group-booking input[type="date"]::-webkit-calendar-picker-indicator, .mec-events-meta-group-booking .mec-booking-shortcode input[type="date"]::-webkit-calendar-picker-indicator, #mec_fes_form input[type="date"]::-webkit-calendar-picker-indicator {background: url("data:image/png;base64,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");
  background-position: center;
  background-size: cover;
  width: 10px;
  height: 12px;
  display: block;}
.mec-color, .mec-color-hover:hover, .mec-color-before *:before, .mec-wrap .mec-color, .mec-wrap .mec-color-hover:hover, .mec-wrap .mec-color-before *:before {color: #40d9f1;}
.mec-bg-color, .mec-bg-color-hover:hover, .mec-wrap .mec-bg-color, .mec-wrap .mec-bg-color-hover:hover {background-color: #40d9f1;}
.mec-border-color, .mec-border-color-hover:hover, .mec-wrap .mec-border-color, .mec-wrap .mec-border-color-hover:hover {border-color: #40d9f1;}
.mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-label-featured, .mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-label-canceled, .mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-label-custom {overflow: hidden;
  position: relative;}
.mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-label-canceled:before {background: #de0404;}
.mec-wrap .mec-custom-data .mec-timetable-t2-content, .mec-wrap .mec-custom-data .mec-timetable-t2-content .mec-event-title, .mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-custom-data.mec-label-canceled, .mec-wrap article:not([class^="mec-event-countdown"]):not([class^="mec-event-cover-"]).mec-custom-data.mec-label-featured {overflow: visible !important;}
.mec-events-meta-group-booking {position: relative;}
.mec-wrap * {-webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;}
.mec-wrap *:before, .mec-wrap *:after {-webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;}
.lity .clearfix, .mec-wrap .clearfix {zoom: 1;}
.lity .clear, .lity .clr, .mec-wrap .clear, .mec-wrap .clr {clear: both;
  display: block;
  overflow: hidden;
  visibility: hidden;}
.mec-container [class*="col-"] img {max-width: 100%;}
.mec-container {margin-right: auto;
  margin-left: auto;
  padding-left: 10px;
  padding-right: 10px;}
.mec-wrap .row {margin-left: -10px;
  margin-right: -10px;}
.mec-wrap .col-xs-1, .mec-wrap .col-sm-1, .mec-wrap .col-md-1, .mec-wrap .col-lg-1, .mec-wrap .col-xs-2, .mec-wrap .col-sm-2, .mec-wrap .col-md-2, .mec-wrap .col-lg-2, .mec-wrap .col-xs-3, .mec-wrap .col-sm-3, .mec-wrap .col-md-3, .mec-wrap .col-lg-3, .mec-wrap .col-xs-4, .mec-wrap .col-sm-4, .mec-wrap .col-md-4, .mec-wrap .col-lg-4, .mec-wrap .col-xs-5, .mec-wrap .col-sm-5, .mec-wrap .col-md-5, .mec-wrap .col-lg-5, .mec-wrap .col-xs-6, .mec-wrap .col-sm-6, .mec-wrap .col-md-6, .mec-wrap .col-lg-6, .mec-wrap .col-xs-7, .mec-wrap .col-sm-7, .mec-wrap .col-md-7, .mec-wrap .col-lg-7, .mec-wrap .col-xs-8, .mec-wrap .col-sm-8, .mec-wrap .col-md-8, .mec-wrap .col-lg-8, .mec-wrap .col-xs-9, .mec-wrap .col-sm-9, .mec-wrap .col-md-9, .mec-wrap .col-lg-9, .mec-wrap .col-xs-10, .mec-wrap .col-sm-10, .mec-wrap .col-md-10, .mec-wrap .col-lg-10, .mec-wrap .col-xs-11, .mec-wrap .col-sm-11, .mec-wrap .col-md-11, .mec-wrap .col-lg-11, .mec-wrap .col-xs-12, .mec-wrap .col-sm-12, .mec-wrap .col-md-12, .mec-wrap .col-lg-12 {position: relative;
  min-height: 1px;
  padding-left: 10px;
  padding-right: 10px;}
.mec-container [class*="col-"].alpha {padding-left: 0;}
.mec-container [class*="col-"].omega {padding-right: 0;}
.mec-wrap .mec-related-events-wrap {margin-left: 0;
  margin-right: 0;}
.mec-related-events {margin-right: -15px;
  margin-left: -15px;}
.mec-related-events-wrap {margin-top: 50px;}
.mec-related-events-wrap h3.mec-rec-events-title:before {content: "";
  background: #2c2f34;
  width: 46px;
  height: 4px;
  position: absolute;
  top: 59px;
  left: 0;}
.mec-related-events-wrap h3.mec-rec-events-title {font-size: 21px;
  font-weight: 600;
  padding: 17px 0;
  margin-bottom: 28px;
  text-transform: uppercase;
  border-bottom: 1px solid #e8e8e8;
  position: relative;}
.mec-related-event-post figure {margin: 0;}
.mec-related-event-post figure img {width: 100%;
  border-radius: var(--mec-primary-border-radius);
  height: auto;}
.mec-related-event-content {background-color: #fff;
  margin: -40px 20px 15px;
  border-radius: var(--mec-primary-border-radius);
  position: relative;
  max-width: 90%;
  padding: 10px 12px 16px 17px;
  border: 1px solid #e6e7e8;}
.mec-related-event-content h5 a {font-size: 16px;
  color: #121212;
  font-weight: 600;
  transition: all 0.3s ease;}
#mec_bfixed_form_field_types .button, #mec_reg_form_field_types .button {position: relative;
  outline: 0;
  border-radius: 50px;
  padding: 2px 21px 2px 31px !important;
  line-height: 1;
  font-size: 11px;
  font-weight: 600;
  color: #40d9f1;
  letter-spacing: 2px;
  height: auto;
  cursor: pointer;
  margin: 0 0 5px 0;
  text-transform: uppercase;
  box-shadow: 0 2px 6px -3px #40d9f1;
  border: 2px solid #cfeff5;}
.mec-cart-coupon input[type="text"] {min-height: 40px;
  font-size: 13px;
  line-height: 38px;
  color: #606367;
  border: 1px solid #e3e5e7;
  border-radius: 3px;
  padding: 0 12px;
  margin-bottom: 0;
  box-shadow: unset;
  width: 330px;
  vertical-align: top;}
.mec-cart ul {padding: 0;
  list-style: none;}
.mec-cart ul h5, .mec-cart ul h6 {margin: 0;
  font-size: 14px;
  color: #000;
  font-weight: inherit;}
.mec-cart ul h6 {padding-left: 0;}
.mec-cart ul h6:before {content: "— ";}
.mec-cart ul h5 {font-weight: 400;
  margin: 0 0 3px 0;}
.mec-cart ul h6:last-child {margin-bottom: 12px;}
.mec-cart table td:nth-child(4) ul li {margin-bottom: 5px;}
.mec-cart table td:nth-child(4) ul li:last-child {margin-bottom: 12px;}
.mec-cart table a {font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  color: #474849;
  fill: #474849;}
.mec-cart table a:hover {color: #000;}
.mec-cart table #mec_cart_total_payable, .mec-cart table .mec-transaction-price {font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.8px;
  text-align: right;
  color: #060607;
  fill: #060607;}
.mec-cart table .mec-cart-remove-transactions svg:hover, .mec-cart table .mec-cart-remove-transactions svg:hover path {color: red !important;
  fill: red !important;
  cursor: pointer;}
.mec-cart table tr {text-align: left;}
.mec-cart table tr th {margin-bottom: 27px;
  margin-top: 0;
  padding: 9px 12px 9px;
  font-size: 14px;
  line-height: 1.8;
  letter-spacing: 0;
  font-weight: 600;
  position: relative;
  color: #000;}
.mec-cart table tr td {padding: 9px 12px 9px;
  font-size: 14px;
  color: #000;}
.mec-cart table tr td a:hover {color: #000;
  text-decoration: underline;}
.mec-cart table tr td, .mec-cart table tr th {background-color: #fff !important;
  border: 0 !important;
  vertical-align: middle;}
.mec-cart table tr {border-top: 1px solid rgba(0, 0, 0, 0.1);}
.mec-cart table tr th:last-child {text-align: right;}
.mec-cart table tr {border-top: 1px solid rgba(0, 0, 0, 0.1);}
.mec-cart table td:nth-child(1) {width: 35px;
  max-width: 35px;}
.mec-cart table td:nth-child(2) {width: 135px;
  max-width: 135px;}
.mec-cart table td:nth-child(3), .mec-cart table td:nth-child(4) {min-width: 200px;
  width: 200px;}
.mec-cart table td:nth-child(5) {font-size: 12px;}
.mec-cart table td:nth-child(6) {text-align: right;
  vertical-align: middle;}
.mec-cart table thead tr th {border-bottom: 1px solid #a3a3a3 !important;}
.mec-cart table thead tr, .mec-cart table thead tr th {border-top: none !important;}
.mec-cart table tfoot tr th:last-child {display: none;}
.mec-checkout .mec-checkout-list {list-style: none;
  padding: 0;
  margin: 40px 0;
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 25px;
  row-gap: 25px;
  padding-bottom: 40px;
  border-bottom: 1px solid #e6e6e6;}
.mec-checkout .mec-checkout-list .mec-list-items {padding: 20px 30px;
  background: #fff;
  border: 1px solid #e6e6e6;
  box-shadow: 0 2px 0 0 rgb(0 0 0 / 2%);}
.mec-checkout .mec-checkout-list .mec-list-items h3 {margin-top: 20px;
  margin-bottom: 12px;
  font-size: 22px;
  line-height: 27px;
  letter-spacing: -0.4px;
  font-weight: 700;
  position: relative;}
.mec-checkout .mec-checkout-list .mec-list-items h5 {font-size: 18px;
  font-weight: 500;
  padding-bottom: 5px;
  display: inline;
  color: #000;
  margin: 0 0 8px 0;}
.mec-checkout .mec-checkout-list .mec-list-items h6 {margin: 0;
  font-size: 16px;
  line-height: 23px;
  color: #8d9599;
  font-weight: inherit;}
.mec-checkout .mec-checkout-list .mec-list-items h6 {padding-left: 0;}
.mec-checkout .mec-checkout-list .mec-list-items h6:before {content: "— ";}
.mec-checkout .mec-checkout-list .mec-list-items ul {list-style: none;
  padding: 15px 0 0 0;}
.mec-checkout .mec-checkout-list .mec-list-items .mec-checkout-price-details {color: #000;
  border: none;
  border-radius: 2px;
  background: #f7f7f7;
  padding: 15px;
  margin: 15px 0 0 0;
  max-width: 100%;}
.mec-checkout .mec-checkout-list .mec-list-items .mec-checkout-price-details li {display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;}
.mec-checkout #mec_cart_total_payable {margin: 0 0 40px 0;
  font-size: 29px;
  line-height: 27px;
  letter-spacing: -0.4px;
  font-weight: 700;
  position: relative;
  color: #39c36e;}
.mec-checkout .mec-gateway-comment {margin-top: 20px;
  margin: 0 0 20px 0;
  color: #616161;
  font-size: 14px;
  line-height: 1.8;}
.mec-checkout label {color: #424242;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0;
  margin: 0 0 4px 0;
  display: block;
  clear: none;
  padding: 7px 1em 3px 0;}
.mec-checkout input[type="radio"]:before {content: "";
  display: inline-block;
  background: #fff;
  border-radius: 24px;
  width: 13px;
  height: 13px;
  cursor: pointer;
  margin: 1px 0 0 0;
  border: 1px solid #d7d8d9;}
.mec-checkout input[type="radio"]:checked:before {border: 0;
  background: #000;}
.mec-checkout input[type="radio"] {margin: 0 4px 0 0;
  font-size: 0;}
.mec-checkout input[type="checkbox"] {float: left;}
.mec-checkout-gateways-wrapper {max-width: 54%;
  margin-bottom: 20px;}
.mec-checkout .mec-book-form-next-button {float: right;
  right: 30%;
  width: 155px;
  text-align: center;}
.mec-checkout-footer {background: #f5f6f7;
  padding: 30px 40px 15px;
  border-radius: 5px;}
.mec-checkout #mec_cart_total_payable {margin: 0 0 15px;
  font-size: 26px;
  line-height: 1;
  letter-spacing: -0.4px;
  font-weight: 700;
  position: relative;
  color: #000000;}
body[class*="mec-theme"] .mec-wrap a {text-decoration: auto;}
.mec-ticket-name-description-wrapper h4 {margin: 0 0 5px 0;}
.mec-ticket-name-description-wrapper h4 .mec-ticket-attendee-counter, .mec-ticket-name-description-wrapper h4 .mec-ticket-name {font-size: 16px;
  font-weight: 500;}
.single-mec-events {overflow-x: hidden;}
.mec-wrap li.mec-event-social-icon a>svg {width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 9px;
  fill: #767676;
  margin-right: 3px;
  transition: all ease 300ms;}
.mec-wrap button.mec-subscribe-to-calendar-btn {margin-top: 20px;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  width: 180px;}
.mec-hourly-schedule-speaker-name, .mec-events-meta-group-countdown .countdown-w span, .mec-single-event .mec-event-meta dt, .mec-hourly-schedule-speaker-job-title, .post-type-archive-mec-events h1, .mec-ticket-available-spots .mec-event-ticket-name, .tax-mec_category h1, .mec-wrap h1, .mec-wrap h2, .mec-wrap h3, .mec-wrap h4, .mec-wrap h5, .mec-wrap h6, .entry-content .mec-wrap h1, .entry-content .mec-wrap h2, .entry-content .mec-wrap h3, .entry-content .mec-wrap h4, .entry-content .mec-wrap h5, .entry-content .mec-wrap h6 {font-family: var(--mec-heading-font-family), Helvetica, Arial, sans-serif;}
.mec-single-event .mec-event-meta .mec-events-event-cost, .mec-event-data-fields .mec-event-data-field-item .mec-event-data-field-value, .mec-event-data-fields .mec-event-data-field-item .mec-event-data-field-name, .mec-wrap .info-msg div, .mec-wrap .mec-error div, .mec-wrap .mec-success div, .mec-wrap .warning-msg div, .mec-breadcrumbs .mec-current, .mec-events-meta-group-tags, .lity-content .mec-events-meta-group-booking .mec-event-ticket-available, .mec-single-event .mec-events-meta-group-booking .mec-event-ticket-available, .mec-single-modern .mec-single-event-bar>div dd, .mec-single-event .mec-event-meta dd, .mec-single-event .mec-event-meta dd a, .mec-next-occ-booking span, .mec-hourly-schedule-speaker-description, .mec-single-event .mec-speakers-details ul li .mec-speaker-job-title, .mec-single-event .mec-speakers-details ul li .mec-speaker-name, .mec-event-data-field-items, .mec-load-more-button, .mec-events-meta-group-tags a, .mec-events-button, .mec-wrap abbr, .mec-event-schedule-content dl dt, .mec-breadcrumbs a, .mec-breadcrumbs span .mec-event-content p, .mec-wrap p {font-family: var(--mec-paragraph-font-family), sans-serif;
  font-weight: 400;}
.mec-skin-carousel-container .mec-event-footer-carousel-type3 .mec-modal-booking-button:hover, .mec-wrap.colorskin-custom .mec-event-sharing .mec-event-share:hover .event-sharing-icon, .mec-wrap.colorskin-custom .mec-event-grid-clean .mec-event-date, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing>li:hover a i, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing>li:hover a svg, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing .mec-event-share:hover .mec-event-sharing-icon, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing li:hover a i, .mec-wrap.colorskin-custom .mec-calendar:not(.mec-event-calendar-classic) .mec-selected-day, .mec-wrap.colorskin-custom .mec-calendar .mec-selected-day:hover, .mec-wrap.colorskin-custom .mec-calendar .mec-calendar-row dt.mec-has-event:hover, .mec-wrap.colorskin-custom .mec-calendar .mec-has-event:after, .mec-wrap.colorskin-custom .mec-bg-color, .mec-wrap.colorskin-custom .mec-bg-color-hover:hover, .colorskin-custom .mec-event-sharing-wrap:hover>li, .mec-wrap.colorskin-custom .mec-totalcal-box .mec-totalcal-view span.mec-totalcalview-selected, .mec-wrap .flip-clock-wrapper ul li a div div.inn, .mec-wrap .mec-totalcal-box .mec-totalcal-view span.mec-totalcalview-selected, .event-carousel-type1-head .mec-event-date-carousel, .mec-event-countdown-style3 .mec-event-date, #wrap .mec-wrap article.mec-event-countdown-style1, .mec-event-countdown-style1 .mec-event-countdown-part3 a.mec-event-button, .mec-wrap .mec-event-countdown-style2, .mec-map-get-direction-btn-cnt input[type="submit"], .mec-booking button, span.mec-marker-wrap, .mec-wrap.colorskin-custom .mec-timeline-events-container .mec-timeline-event-date:before, .mec-has-event-for-booking.mec-active .mec-calendar-novel-selected-day, .mec-booking-tooltip-container .mec-booking-calendar-date.mec-active, .mec-booking-tooltip-container .mec-booking-calendar-date:hover, .mec-ongoing-normal-label, .mec-calendar .mec-has-event:after, .mec-events-progress-bar progress:after, .mec-events-progress-bar progress::-webkit-progress-value {background-color: var(--mec-color-skin);}
.mec-skin-carousel-container .mec-event-footer-carousel-type3 .mec-modal-booking-button:hover, .mec-timeline-month-divider, .mec-wrap.colorskin-custom .mec-single-event .mec-speakers-details ul li .mec-speaker-avatar a:hover img, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing>li:hover a i, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing>li:hover a svg, .mec-wrap.colorskin-custom .mec-event-list-modern .mec-event-sharing .mec-event-share:hover .mec-event-sharing-icon, .mec-wrap.colorskin-custom .mec-event-list-standard .mec-month-divider span:before, .mec-wrap.colorskin-custom .mec-single-event .mec-social-single:before, .mec-wrap.colorskin-custom .mec-single-event .mec-frontbox-title:before, .mec-wrap.colorskin-custom .mec-calendar .mec-calendar-events-side .mec-table-side-day, .mec-wrap.colorskin-custom .mec-border-color, .mec-wrap.colorskin-custom .mec-border-color-hover:hover, .colorskin-custom .mec-single-event .mec-frontbox-title:before, .colorskin-custom .mec-single-event .mec-wrap-checkout h4:before, .colorskin-custom .mec-single-event .mec-events-meta-group-booking form>h4:before, .mec-wrap.colorskin-custom .mec-totalcal-box .mec-totalcal-view span.mec-totalcalview-selected, .mec-wrap .mec-totalcal-box .mec-totalcal-view span.mec-totalcalview-selected, .event-carousel-type1-head .mec-event-date-carousel:after, .mec-wrap.colorskin-custom .mec-events-masonry-cats a.mec-masonry-cat-selected, .mec-marker-infowindow-wp .mec-marker-infowindow-count, .mec-wrap.colorskin-custom .mec-events-masonry-cats a:hover, .mec-has-event-for-booking .mec-calendar-novel-selected-day, .mec-booking-tooltip-container .mec-booking-calendar-date.mec-active, .mec-booking-tooltip-container .mec-booking-calendar-date:hover, .mec-virtual-event-history h3:before, .mec-booking-tooltip-container .mec-booking-calendar-date:hover, .mec-calendar-day.mec-active .mec-booking-tooltip-container .mec-booking-calendar-date.mec-active, .mec-rsvp-form-box form>h4:before, .mec-wrap .mec-box-title::before, .mec-box-title::before, .lity-content .mec-booking-progress-bar li.mec-active:after, .mec-single-event .mec-frontbox-title:before, .mec-single-event .mec-wrap-checkout h4:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout h4:before, .lity-content .mec-events-meta-group-booking form>h4:before, .mec-single-event .mec-events-meta-group-booking form>h4:before, .mec-events-meta-group-booking .mec-booking-shortcode form>h4:before, .lity-content .mec-events-meta-group-booking .mec-wrap-checkout h4:before, .mec-single-event .mec-events-meta-group-booking .mec-wrap-checkout h4:before, .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout h4:before {border-color: var(--mec-color-skin);}
.mec-wrap .button, .mec-wrap button, .mec-wrap input[type="submit"], .mec-wrap input[type="reset"], .mec-wrap input[type="button"], .lity-content .button, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove), .lity-content input[type="submit"], .lity-content input[type="reset"], .lity-content input[type="button"], .lity-content input[type="submit"], .mec-nextprev-next-button, .mec-nextprev-prev-button, .mec-form-row .mec-book-form-next-button.mec-book-form-pay-button {color: var(--mec-color-skin);
  background: var(--mec-color-skin-rgba-1);
  border-color: var(--mec-color-skin-rgba-1);}
.mec-wrap .button:hover, .mec-wrap a.button:hover, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove):hover, .mec-wrap input[type="submit"]:hover, .mec-wrap input[type="reset"]:hover, .mec-wrap input[type="button"]:hover, .lity-content .button:hover, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove):hover, .lity-content input[type="submit"]:hover, .lity-content input[type="reset"]:hover, .lity-content input[type="button"]:hover, .lity-content input[type="submit"]:hover, .mec-nextprev-next-button:hover, .mec-nextprev-prev-button:hover {border-color: var(--mec-color-skin);}
.mec-single-event .mec-events-meta-group-booking input[type="radio"]:checked:before, .mec-events-meta-group-booking .mec-booking-shortcode input[type="radio"]:checked:before, .lity-container .mec-events-meta-group-booking input[type="radio"]:checked:before {background: var(--mec-color-skin);
  box-shadow: 0 0 0 2px var(--mec-color-skin-rgba-1);}
.mec-wrap .button svg path, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.mec-googlemap-details button):not(.mec-googlemap-details button):not(.elementor-add-section-inner button):not(.dismissButton):not([class*="et-"]):not(.mec-seat-builder-booking__seat-card-remove) svg path, .mec-wrap input[type="submit"] svg path, .mec-wrap input[type="reset"] svg path, .mec-wrap input[type="button"] svg path, .lity-content .button svg path, .lity-content button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next) svg path, .lity-content input[type="submit"] svg path, .lity-content input[type="reset"] svg path, .lity-content input[type="button"] svg path, .mec-nextprev-next-button svg path, .mec-nextprev-prev-button svg path {fill: var(--mec-color-skin);}
.mec-wrap .button svg.back path, .mec-wrap button:not(.owl-dot):not(.gm-control-active):not(.mejs):not(.owl-prev):not(.owl-next):not(.et-fb-button) svg.back path {fill: #636f72;}
.mec-wrap h1 a, .mec-wrap h2 a, .mec-wrap h3 a, .mec-wrap h4 a, .mec-wrap h5 a, .mec-wrap h6 a, .entry-content .mec-wrap h1 a, .entry-content .mec-wrap h2 a, .entry-content .mec-wrap h3 a, .entry-content .mec-wrap h4 a, .entry-content .mec-wrap h5 a, .entry-content .mec-wrap h6 a, .mec-wrap .mec-calendar .mec-event-article .mec-event-title a, .mec-wrap .mec-event-grid-simple .mec-event-title a, .mec-wrap .mec-event-grid-modern .mec-event-title a, .mec-wrap .mec-event-grid-minimal .mec-event-title a, .mec-wrap .mec-event-grid-clean .mec-event-title a, .mec-wrap .mec-event-grid-classic .mec-event-title a, .mec-wrap .mec-event-list-standard .mec-event-title a, .mec-wrap .mec-wrap .mec-event-list-modern .mec-event-title a, .mec-wrap .mec-event-list-minimal .mec-event-title a, .mec-wrap .mec-event-list-classic .mec-event-title a {color: var(--mec-title-color);}
.mec-theme-saga .mec-single-event .mec-frontbox-title, .mec-theme-saga .mec-single-event .mec-wrap-checkout h4, .mec-theme-saga .lity-content .mec-events-meta-group-booking .mec-wrap-checkout h4, .mec-theme-saga .mec-single-event .mec-events-meta-group-booking .mec-wrap-checkout h4, .mec-theme-saga .mec-events-meta-group-booking .mec-booking-shortcode .mec-wrap-checkout h4, .mec-theme-saga .lity-content .mec-events-meta-group-booking form>h4, .mec-single-event .mec-events-meta-group-booking form>h4, .mec-theme-saga .mec-events-meta-group-booking .mec-booking-shortcode form>h4 {text-align: center;}
.mec-event-meta img.mec-custom-image-icon, .mec-single-event-bar img.mec-custom-image-icon, .mec-event-banner img.mec-custom-image-icon {width: 20px;}
body.rtl .mec-wrap {font-family: vazirmatn;}
.mec-util-hidden {display: none;}
span.mec-booking-field-required {color: red;
  margin-left: 10px;}
.mec-book-ticket-container .mec-red-notification input, .mec-booking .mec-red-notification input, .mec-booking .mec-red-notification textarea {border: 1px solid #ff3c3c !important;}
.mec-booking .mec-red-notification input[type="radio"], .mec-booking .mec-red-notification input[type="checkbox"] {outline: 1px solid #ff3c3c !important;}
.has-vivid-red-color {color: #f44336;}
.is-invalid {border-color: #ff3c3c;}
/* GDQ theme customizer CSS (booking-relevant subset, verbatim) */
.mec-wrap {font-size: inherit; line-height: inherit;
  --mec-heading-font-family: inherit;
  --mec-paragraph-font-family: inherit;
  --mec-color-skin: #007d8f;
  --mec-color-skin-rgba-1: rgba(0, 125, 143, 0.25);
  --mec-color-skin-rgba-2: rgba(0, 125, 143, 0.5);
  --mec-color-skin-rgba-3: rgba(0, 125, 143, 0.75);
  --mec-color-skin-rgba-4: rgba(0, 125, 143, 0.11);
  --mec-primary-border-radius: 12px;
  --mec-secondary-border-radius: 3px;
  --mec-container-normal-width: 1196px;
  --mec-container-large-width: 1690px;}
.mec-wrap .mec-single-event .mec-events-meta-group-booking button.mec-book-form-back-button#mec-book-form-back-btn-step-2 img.mec-svg-icon {display: none;}
.mec-booking-button, .mec-booking button[type=submit], .mec-book-form-next-button {background-color: #007D8F !important; height: 45px !important; padding: 0 !important; border-radius: 50px !important;}
.mec-events-meta-group-booking input[type=text], .mec-events-meta-group-booking input[type=email], .mec-events-meta-group-booking input[type=number] {background: #fff !important; box-shadow: none !important; width: 100% !important; margin-top: 20px !important; font-size: 16px !important; color: #000 !important;}
.mec-event-ticket-available {font-size: 16px !important; padding-top: 10px; color: #000 !important;}
.mec-event-ticket-name {color: #000 !important;}
.mec-events-meta-group-booking form > h4:before {border: none !important;}
.mec-events-meta-group-booking input[type=radio] {line-height: 0 !important;}
.mec-events-meta-group-booking form > h4 {text-align: left !important; font-size: 20px !important; text-transform: none !important; border: none !important;}
.mec-checkout-cart-link, .mec-list-items h3 {display: none !important;}
.mec-checkout .mec-checkout-list .mec-list-items {border: none; box-shadow: none;}
.mec-checkout .mec-checkout-list .mec-list-items h6:before {content: '';}
.mec-checkout-footer {background-color: #fff !important;}
.mec-single-event .mec-events-meta-group-booking input[type='radio']:before, .mec-single-event .mec-events-meta-group-booking input[type='radio']:checked:before {box-shadow: none;}
.mec-book-reg-field-text label, .mec-events-meta-group-booking label {font-size: 16px !important;}
.mec-book-tickets-container h4 {font-size: 22px !important;}
.mec-book-reg-field-agreement {margin-bottom: 40px;}
.mec-checkout-tickets-wrapper h5 {font-weight: 700 !important;}
.mec-checkout-tickets-wrapper h6 {color: #000 !important; text-transform: none; font-size: 18px !important;}
.mec-checkout-tickets-wrapper, #mec_cart_total_payable, .mec-gateway-comment, #mec_cart_transactions_table * {font-size: 18px !important; font-weight: 400;}
.mec-checkout-form-gateway-label, .mec-checkout-price-details-wrapper {display: none;}
#mec_cart_transactions_table {border: none;}
#mec_cart_transactions_table th, #mec_cart_transactions_table td {width: auto;}
#mec_cart_transactions_table .mec-cart-event-info img {display: none;}
#mec_cart_transactions_table ul {margin-left: 0;}
.mec-checkout .mec-book-form-next-button {width: 70% !important;}
.mec-cart table tr th {margin-bottom: 0 !important;}
@media (min-width: 1024px) {
  .mec-cart #mec_cart_transactions_table thead, .mec-cart #mec_cart_transactions_table tbody {display: grid;}
  .mec-cart #mec_cart_transactions_table tr {display: grid; grid-template-columns: 40px 170px 1fr 1fr 1fr 80px;}
}
/* GDQ site overrides (MEC settings: styles.CSS) */
.mec-booking-button, .mec-book-form-back-button, .mec-book-form-next-button {color: #fff !important;}
.mec-ticket-available-spots .mec-ticket-style-row-section-1 {display: none !important;}
.mec-ticket-icon-wrapper {display: none !important;}
.mec-events-meta-group-booking input[type=text], .mec-events-meta-group-booking input[type=email], .mec-events-meta-group-booking input[type=number] {margin-top: 0 !important;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-event-ticket-name {font-size: 18px !important;}
.mec-ticket-style-row.mec-ticket-available-spots {flex-direction: column !important;
  align-items: flex-start !important;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row .mec-ticket-style-row-section-3 {margin-left: 0 !important;}
.mec-booking .mec-event-tickets-list .mec-ticket-style-row input[type=number] {width: 100% !important;}
.mec-booking button, .mec-booking input[type=submit], .mec-booking input[type=button] {color: #fff; border-color: #007d8f; background-color: #007d8f;}
</style>
